import moment from 'moment';
import { ActionTypes } from '../../constants/actionTypes/hotelSearch';
import store from '../../stores/hotelSearch';
import * as hotelSearchService from '../../../services/hotelSearch';
import { fetchAttributeCollection } from '../../../services/attributeCollection';

import {
  getSelectedDuration,
  getSelectedDepartureDate,
  getSelectedReturnDate,
  getPriceCalendarDataKey,
} from '../../../utils/actionHelper';

export const initHotelSearch = (siteSettings, hotelPartId) => {
  return async (dispatch) => {
    const { siteId, alias, apiUrl, baseUrl } = siteSettings;

    dispatch({
      type: ActionTypes.HOTEL_SEARCH_CONTROL_INIT,
      siteSettings,
    });

    await hotelSearchService.init(siteId, apiUrl, hotelPartId);
    const labels = await fetchAttributeCollection(baseUrl, siteId, alias);

    dispatch({
      type: ActionTypes.HOTEL_SEARCH_CONTROL_INIT_SUCCESS,
      labels,
    });
  };
};

export const getInitState = (siteSettings, cookieId) => {
  return async (dispatch) => {
    dispatch({
      type: ActionTypes.HOTEL_SEARCH_CONTROL_GET_INIT_STATE,
    });

    const data = await hotelSearchService.getInitState(cookieId);

    if (data.searchInfoMissing) {
      dispatch({
        type: ActionTypes.HOTEL_SEARCH_CONTROL_SEARCH_INFO_MISSING,
        data,
      });
      return;
    }

    dispatch({
      type: ActionTypes.HOTEL_SEARCH_CONTROL_GET_INIT_STATE_SUCCESS,
      data,
      siteSettings,
    });
  };
};

export const getDepartureDates = (departureId, duration) => {
  return async (dispatch) => {
    dispatch({
      type: ActionTypes.HOTEL_SEARCH_CONTROL_GET_DEPARTURE_DATES,
    });

    const departureDates = await hotelSearchService.getDepartureDates(departureId, duration.defaultDuration);
    const prevSelectedDepartureDate = store.getState().selectedDepartureDate;
    const prevSelectedReturnDate = store.getState().selectedReturnDate;
    const selectedDepartureDate = duration.bookingType === 'independent'
      ? prevSelectedDepartureDate
      : getSelectedDepartureDate(departureDates, prevSelectedDepartureDate);
    const selectedReturnDate = getSelectedReturnDate(selectedDepartureDate, duration, prevSelectedReturnDate);

    dispatch({
      type: ActionTypes.HOTEL_SEARCH_CONTROL_GET_DEPARTURE_DATES_SUCCESS,
      departureDates,
      selectedDepartureDate,
      selectedReturnDate,
    });
  };
};

const getCalendarPrices = (selectedValues) => {
  return async (dispatch) => {
    const {
      siteSettings,
      priceCalendarData,
      selectedDeparture,
      selectedHotelId,
      selectedDuration,
      showPriceCalendar,
      showDirectFlights,
    } = selectedValues;

    const shouldFetchPrices = showPriceCalendar && selectedDuration.hasCalendar;
    if (!shouldFetchPrices) {
      return;
    }

    // Get calendar prices
    const departureAirportId = selectedDeparture.itemId;
    const priceCalendarKey = getPriceCalendarDataKey(departureAirportId, 0, selectedHotelId, selectedDuration, showDirectFlights);
    // Fetch prices if not fetched previously.
    if (!priceCalendarData[priceCalendarKey]) {
      const minDepDate = moment().add(siteSettings.datePicker.dpBookDaysAhead, 'days');
      const maxDepDate = moment().add(siteSettings.datePicker.dpMaxDaysToReturn, 'days');
      let dateToFetch = minDepDate;
      const tempPriceCalendarData = {};
      while (!dateToFetch.isAfter(maxDepDate)) {
        // eslint-disable-next-line no-await-in-loop
        const data = await hotelSearchService.getPriceCalendar(
          departureAirportId,
          selectedHotelId,
          dateToFetch.format('YYYY-MM-DD'),
          selectedDuration.defaultDuration + 1,
          showDirectFlights,
        );
        if (data && data.calendars && data.calendars.length > 0) {
          data.calendars.forEach((calendarMonth) => {
            const dataMonth = calendarMonth.month;
            tempPriceCalendarData[dataMonth] = calendarMonth;
          });
          if (data.calendars.length < 4) {
            // We should get calendar for 4 months in each request.
            // If less than 4 months is returned, then no more calendar data exists and we should stop fetching new data.
            break;
          }
          const lastFetchedCalenderMonth = moment(data.calendars[data.calendars.length - 1].month);
          if (!lastFetchedCalenderMonth.isAfter(dateToFetch)) {
            break;
          }
          dateToFetch = lastFetchedCalenderMonth.add(1, 'M');
        } else {
          break;
        }
      }

      priceCalendarData[priceCalendarKey] = [];
      Object.keys(tempPriceCalendarData).forEach((key) => {
        priceCalendarData[priceCalendarKey].push(tempPriceCalendarData[key]);
      });
      dispatch({
        type: ActionTypes.HOTEL_SEARCH_CONTROL_GET_PRICE_CALENDAR_DATA_SUCCESS,
        priceCalendarData,
      });
    }
  }
};

export const setPreviousDuration = (previousDuration) => {
  return (dispatch) => {
    dispatch({
      type: ActionTypes.HOTEL_SEARCH_CONTROL_SET_PREVIOUS_DURATION,
      previousDuration,
    });
  }
}

export const getDurations = (departureId) => {
  return async (dispatch) => {
    dispatch({
      type: ActionTypes.HOTEL_SEARCH_CONTROL_GET_DURATIONS,
    });

    const durationsData = await hotelSearchService.getDurations(departureId);
    const durations = durationsData ? durationsData.durations : [];
    const prevSelectedDuration = store.getState().selectedDuration;
    const selectedDuration = getSelectedDuration(durations, prevSelectedDuration);
    const options = durationsData ? durationsData.options : [];

    dispatch({
      type: ActionTypes.HOTEL_SEARCH_CONTROL_GET_DURATIONS_SUCCESS,
      durations,
      selectedDuration,
      options,
    });

    dispatch(setPreviousDuration(null));

    dispatch(getDepartureDates(departureId, selectedDuration));

    const state = store.getState();
    const newState = {
      ...state,
      selectedDuration,
    };
    dispatch(getCalendarPrices(newState));
  };
};

export const changeDeparture = (departure) => {
  return (dispatch) => {
    dispatch({
      type: ActionTypes.HOTEL_SEARCH_CONTROL_CHANGE_DEPARTURE,
      departure,
    });

    dispatch(getDurations(departure.itemId));

    const state = store.getState();
    const newState = {
      ...state,
      selectedDeparture: departure,
    };
    dispatch(getCalendarPrices(newState));
  };
};

export const changeDuration = (duration, clearPreviousDuration = true) => {
  return (dispatch) => {
    dispatch({
      type: ActionTypes.HOTEL_SEARCH_CONTROL_CHANGE_DURATION,
      duration,
    });

    if (clearPreviousDuration) {
      dispatch(setPreviousDuration(null));
    }

    const state = store.getState();
    const selectedDepartureId = state.selectedDeparture.itemId;
    dispatch(getDepartureDates(selectedDepartureId, duration));

    const newState = {
      ...state,
      selectedDuration: duration,
    };
    dispatch(getCalendarPrices(newState));
  };
};

export const changeDates = (departureDate, returnDate) => {
  return (dispatch) => {
    const duration = store.getState().selectedDuration;
    const selectedReturnDate = returnDate || getSelectedReturnDate(departureDate, duration);

    dispatch({
      type: ActionTypes.HOTEL_SEARCH_CONTROL_CHANGE_DATES,
      selectedDepartureDate: departureDate,
      selectedReturnDate,
    });
  };
};

export const changeRoomDistribution = (roomDistribution, selectedNumberOfRooms) => {
  return {
    type: ActionTypes.HOTEL_SEARCH_CONTROL_CHANGE_ROOM_DISTRIBUTION,
    roomDistribution,
    selectedNumberOfRooms,
  };
};

export const setShowPriceCalendar = (showPriceCalendar) => {
  return async (dispatch) => {
    dispatch ({
      type: ActionTypes.HOTEL_SEARCH_CONTROL_SET_SHOW_PRICE_CALENDAR,
      showPriceCalendar,
    });

    const newState = {
      ...store.getState(),
      showPriceCalendar,
    }

    dispatch(getCalendarPrices(newState));
  }
};

export const setShowDirectFlights = (showDirectFlights) => {
  return async (dispatch) => {
    dispatch({
      type: ActionTypes.HOTEL_SEARCH_CONTROL_SET_SHOW_DIRECT_FLIGHTS,
      showDirectFlights,
    });

    const newState = {
      ...store.getState(),
      showDirectFlights,
    };

    dispatch(getCalendarPrices(newState));
  };
};
